import { Css, useModal, useSnackbar, useTestIds } from "@homebound/beam";
import { observer } from "mobx-react";
import { useCallback, useState } from "react";
import { InsideMapsConfigVersionTarget } from "src/generated/graphqlTypes";
import { useInsideMapsConfig } from "../hooks/useInsideMapsConfig";
import { useSaveInsideMapsConfigMutation } from "../queries/insideMapsMutations.generated";
import { SaveConfirmationDialog } from "./SaveConfirmationDialog";

export const SaveToBlueprint = observer(function SaveToBlueprint() {
  const store = useInsideMapsConfig();
  const tid = useTestIds({});
  const { triggerNotice } = useSnackbar();
  const { openModal } = useModal();
  const [saving, setSaving] = useState(false);
  const [saveInsideMapsConfig] = useSaveInsideMapsConfigMutation();

  const doSave = useCallback(async () => {
    setSaving(true);
    try {
      const configJson = store.toJson();
      await saveInsideMapsConfig({
        variables: {
          input: {
            productOfferingId: store.selectedProductOfferingId!,
            insideMapsConfig: configJson,
            version: InsideMapsConfigVersionTarget.Active,
          },
        },
      });
      store.markSaved();
      triggerNotice({ message: "Saved to Blueprint successfully!", icon: "success" });
    } catch (error) {
      console.error("Failed to save to Blueprint:", error);
      triggerNotice({ message: "Failed to save to Blueprint. Please try again.", icon: "error" });
    } finally {
      setSaving(false);
    }
  }, [store, saveInsideMapsConfig, triggerNotice]);

  const handleSave = useCallback(() => {
    if (!store.selectedProductOfferingId) {
      triggerNotice({ message: "Please select a product offering first.", icon: "error" });
      return;
    }
    if (store.cameras.length === 0) {
      triggerNotice({ message: "No data to save.", icon: "error" });
      return;
    }

    openModal({ content: <SaveConfirmationDialog changes={store.changes} onConfirm={doSave} />, size: "lg" });
  }, [store, doSave, triggerNotice, openModal]);

  const handleCopyToClipboard = useCallback(() => {
    const configJson = store.toJson();
    void navigator.clipboard.writeText(configJson).then(() => {
      triggerNotice({ message: "Config copied to clipboard!", icon: "success" });
    });
  }, [store, triggerNotice]);

  return (
    <div css={Css.df.aic.gap2.$} {...tid.saveToBlueprint}>
      <button
        css={
          Css.pyPx(8).pxPx(16).br4.white.smSb.cursorPointer.add("backgroundColor", "#16a34a").add("border", "none").$
        }
        onClick={handleCopyToClipboard}
      >
        Copy Data
      </button>
      <button
        css={
          Css.pyPx(8).pxPx(16).br4.white.smSb.cursorPointer.add("backgroundColor", "#ea580c").add("border", "none").$
        }
        onClick={handleSave}
        disabled={saving}
      >
        {saving ? "Saving..." : "Save to Blueprint"}
      </button>
      {store.isDirty && <span css={Css.xs.yellow600.$}>Unsaved changes</span>}
    </div>
  );
});
